import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Button, Space, Table, Tag, message, Modal, Empty, Tabs, Popconfirm, Input, Typography } from 'antd';
import { UserOutlined, LogoutOutlined, DownloadOutlined, EyeOutlined, SettingOutlined, FileTextOutlined, DatabaseOutlined, DeleteOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getTasksByUser, deleteTask, batchDeleteTasks, getTask, type TaskResponse } from '../../utils/taskManager';
import { isProfileVerifyRequired, resetProfileVerifyGuide } from '../../api/taskManagerApi';
import TaskResultRenderer from '../../components/results/TaskResultRenderer';
import { formatUTCToLocal } from '../../utils/timeUtils';

const { TabPane } = Tabs;

const ProfilePage: React.FC = () => {
  const { user, logout, getProfileVerifyStatus, requestProfileCode, verifyProfileCode } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskResponse | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [totalFetched, setTotalFetched] = useState(false);
  const [fetchingDetail, setFetchingDetail] = useState(false);
  // 个人中心邮箱验证状态
  const [verified, setVerified] = useState<boolean | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  // 获取用户的历史任务 - 支持分页
  const fetchUserTasks = async (page: number = 1, pageSize: number = 20) => {
    if (!user) return;
    
    setLoading(true);
    try {
      // 计算分页参数
      const skip = (page - 1) * pageSize;
      
      // 使用后端分页查询
      const { getTasksByUser, getTasks } = await import('../../utils/taskManager');
      
      // 获取用户任务数据
      const userTasks = await getTasksByUser(user.id, skip, pageSize);
      
      // 仅在第一次加载时获取总数，避免重复请求
      let total = pagination.total;
      if (!totalFetched || page === 1) {
        // 获取总数，实际应该有专门的count接口
        const allUserTasks = await getTasksByUser(user.id, 0, 1000);
        total = allUserTasks?.length || 0;
        setTotalFetched(true);
      }
      
      setTasks(userTasks || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: pageSize,
        total: total
      }));
    } catch (error) {
      console.error('获取用户任务失败:', error);
      if (isProfileVerifyRequired(error)) {
        // 验证状态可能已过期，退回验证界面
        setVerified(false);
      } else {
        message.error('获取任务历史失败');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setVerified(null);
      return;
    }
    let cancelled = false;
    getProfileVerifyStatus().then(ok => {
      if (!cancelled) setVerified(ok);
    });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (user && verified) {
      // 延迟加载任务数据，优先显示用户信息
      const timer = setTimeout(() => {
        fetchUserTasks(1, pagination.pageSize);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, verified]);

  // 发送个人中心访问验证码
  const handleSendCode = async () => {
    setSendingCode(true);
    try {
      const result = await requestProfileCode();
      if (result.success) {
        setCodeSent(true);
        message.success(result.message);
      } else {
        message.error(result.message);
      }
    } finally {
      setSendingCode(false);
    }
  };

  // 校验验证码
  const handleVerifyCode = async () => {
    if (!/^\d{6}$/.test(verifyCode.trim())) {
      message.warning('请输入 6 位数字验证码');
      return;
    }
    setVerifying(true);
    try {
      const result = await verifyProfileCode(verifyCode.trim());
      if (result.success) {
        message.success(result.message);
        setVerifyCode('');
        setCodeSent(false);
        setVerified(true);
        // 验证成功，清除「稍后再说」的忽略标记，恢复其他页面的引导弹窗
        resetProfileVerifyGuide();
      } else {
        message.error(result.message);
      }
    } finally {
      setVerifying(false);
    }
  };

  // 打开弹窗时获取完整任务详情（含结果）
  useEffect(() => {
    const loadTaskDetail = async () => {
      if (!modalVisible || !selectedTask?.id || fetchingDetail) return;
      try {
        setFetchingDetail(true);
        const full = await getTask(selectedTask.id);
        let finalTask = full;
        // 如果没有内联结果但提供了结果URL，尝试获取
        if (!full.result && full.result_url) {
          try {
            const resp = await fetch(full.result_url);
            if (resp.ok) {
              const data = await resp.json().catch(() => null);
              if (data) {
                finalTask = { ...full, result: data } as TaskResponse;
              }
            }
          } catch (e) {
            // 忽略外链结果获取失败，保持原样
          }
        }
        setSelectedTask(finalTask);
      } finally {
        setFetchingDetail(false);
      }
    };

    loadTaskDetail();
  }, [modalVisible, selectedTask?.id]);

  // 分页变化处理
  const handleTableChange = (paginationConfig: any) => {
    fetchUserTasks(paginationConfig.current, paginationConfig.pageSize);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  // 删除单个任务
  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      message.success('任务已标记删除');
      // 刷新任务列表
      fetchUserTasks(pagination.current, pagination.pageSize);
      // 如果在删除选中的任务，清除选择
      setSelectedRowKeys(prev => prev.filter(key => key !== taskId));
    } catch (error) {
      message.error('删除任务失败');
      console.error('删除任务失败:', error);
    }
  };

  // 批量删除任务
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的任务');
      return;
    }

    try {
      await batchDeleteTasks(selectedRowKeys as string[]);
      message.success(`成功标记删除 ${selectedRowKeys.length} 个任务`);
      // 刷新任务列表
      fetchUserTasks(pagination.current, pagination.pageSize);
      // 清空选择
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除任务失败');
      console.error('批量删除任务失败:', error);
    }
  };

  const handleExportData = () => {
    if (tasks.length === 0) {
      message.warning('暂无任务数据可导出');
      return;
    }

    const dataStr = JSON.stringify(tasks, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tasks_${user?.email}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    message.success('任务数据已导出');
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'pending': 'default',
      'queued': 'processing',
      'running': 'processing',
      'completed': 'success',
      'failed': 'error',
      'cancelled': 'warning',
      'timeout': 'error'
    };
    return colorMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      'pending': '等待中',
      'queued': '队列中',
      'running': '运行中',
      'completed': '已完成',
      'failed': '失败',
      'cancelled': '已取消',
      'timeout': '超时'
    };
    return textMap[status] || status;
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: '任务类型',
      key: 'task_type_display',
      render: (_: any, record: TaskResponse) => {
        const handlerName = (record as any).handler_name || '';
        const displayName = handlerName || record.service_name || '-';
        return <Tag color="blue">{displayName}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number) => `${progress}%`,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => formatUTCToLocal(time),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: TaskResponse) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedTask(record);
              setModalVisible(true);
            }}
          >
            查看详情
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除这个任务吗？删除后可在管理员页面恢复。"
            onConfirm={() => handleDeleteTask(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!user) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Card>
          <Empty
            description="请先登录"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={() => navigate('/auth/login')}>
              去登录
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  if (verified === null) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Card loading />
      </div>
    );
  }

  if (!verified) {
    return (
      <div style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card
          title={
            <Space>
              <SafetyCertificateOutlined />
              安全验证
            </Space>
          }
          style={{ width: 460 }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Text>
              为保护你的数据，进入个人中心需要验证邮箱归属。验证码将发送至：
            </Typography.Text>
            <Typography.Text strong>{user.email}</Typography.Text>
            <Button
              type={codeSent ? 'default' : 'primary'}
              block
              loading={sendingCode}
              onClick={handleSendCode}
            >
              {codeSent ? '重新发送验证码' : '发送验证码'}
            </Button>
            <Input
              placeholder="请输入 6 位验证码"
              maxLength={6}
              value={verifyCode}
              onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              onPressEnter={handleVerifyCode}
              style={{ letterSpacing: '4px', textAlign: 'center' }}
            />
            <Button
              type="primary"
              block
              loading={verifying}
              disabled={verifyCode.length !== 6}
              onClick={handleVerifyCode}
            >
              验证并进入
            </Button>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              验证码 10 分钟内有效。若未收到邮件，请检查垃圾邮件目录。
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              验证一次即可，之后各分析页面的历史任务列表也会正常显示。
            </Typography.Text>
            <Button type="link" block onClick={() => navigate('/')}>
              返回首页
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 用户信息卡片 */}
        <Card 
          title={
            <Space>
              <UserOutlined />
              个人信息
            </Space>
          }
          extra={
            <Space>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />}
                onClick={handleExportData}
              >
                导出数据
              </Button>
              <Button 
                danger 
                icon={<LogoutOutlined />}
                onClick={handleLogout}
              >
                注销登录
              </Button>
            </Space>
          }
        >
          <Descriptions column={2}>
            <Descriptions.Item label="邮箱">{user.email}</Descriptions.Item>
            <Descriptions.Item label="用户ID">{user.id}</Descriptions.Item>
            <Descriptions.Item label="注册时间">
              {formatUTCToLocal(user.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="最后登录">
              {user.last_login_at 
                ? formatUTCToLocal(user.last_login_at) 
                : '首次登录'}
            </Descriptions.Item>
            <Descriptions.Item label="邮箱验证">
              <Tag color={user.email_verified_at ? 'success' : 'warning'}>
                {user.email_verified_at ? '已验证' : '未验证'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="账户状态">
              <Tag color={user.is_active ? 'success' : 'error'}>
                {user.is_active ? '活跃' : '禁用'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 任务历史卡片 */}
        <Card 
          title={`任务历史 (共 ${pagination.total} 条记录)`}
          extra={
            selectedRowKeys.length > 0 && (
              <Popconfirm
                title="批量删除确认"
                description={`确定要删除选中的 ${selectedRowKeys.length} 个任务吗？`}
                onConfirm={handleBatchDelete}
                okText="确认"
                cancelText="取消"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                >
                  批量删除 ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )
          }
        >
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            loading={loading}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
              selections: [
                Table.SELECTION_ALL,
                Table.SELECTION_INVERT,
                Table.SELECTION_NONE,
              ],
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range?.[0]}-${range?.[1]} 条，共 ${total} 条记录`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
              onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size })
            }}
          />
        </Card>
      </Space>

      {/* 任务详情弹窗 */}
      <Modal
        title={`任务详情 - ${selectedTask?.name || ''}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={1200}
        centered
      >
        {selectedTask && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* 完整的任务详情信息 */}
            <Descriptions 
              column={2} 
              bordered 
              size="small"
              style={{ marginBottom: 16 }}
              labelStyle={{ width: '120px', fontWeight: 'bold' }}
              contentStyle={{ wordBreak: 'break-all' }}
            >
              <Descriptions.Item label="任务名称" span={2}>{selectedTask.name}</Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>{selectedTask.description || '无'}</Descriptions.Item>
              <Descriptions.Item label="服务名称">{selectedTask.service_name}</Descriptions.Item>
              <Descriptions.Item label="任务类型">{selectedTask.task_type}</Descriptions.Item>
              <Descriptions.Item label="处理器" span={2}>{selectedTask.handler_name}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={getStatusColor(selectedTask.status)}>
                  {getStatusText(selectedTask.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="进度">{selectedTask.progress}%</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {formatUTCToLocal(selectedTask.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="开始时间">
                {selectedTask.started_at 
                  ? formatUTCToLocal(selectedTask.started_at) 
                  : '未开始'}
              </Descriptions.Item>
              <Descriptions.Item label="完成时间" span={2}>
                {selectedTask.completed_at 
                  ? formatUTCToLocal(selectedTask.completed_at) 
                  : '未完成'}
              </Descriptions.Item>
              <Descriptions.Item label="优先级">{selectedTask.priority}</Descriptions.Item>
              <Descriptions.Item label="重试次数">
                {selectedTask.retry_count} / {selectedTask.max_retries}
              </Descriptions.Item>
              <Descriptions.Item label="超时时间" span={2}>{selectedTask.timeout}秒</Descriptions.Item>
            </Descriptions>

            {selectedTask.error && (
              <div style={{ marginBottom: '16px' }}>
                <Card size="small" style={{ borderColor: '#ff4d4f' }}>
                  <h4 style={{ marginBottom: '8px', color: '#ff4d4f' }}>错误信息</h4>
                  <div style={{ 
                    color: '#ff4d4f', 
                    fontFamily: 'monospace',
                    background: '#fff2f0',
                    border: '1px solid #ffccc7',
                    borderRadius: '4px',
                    padding: '12px',
                    maxHeight: '200px',
                    overflow: 'auto',
                    wordBreak: 'break-all'
                  }}>
                    {selectedTask.error}
                  </div>
                </Card>
              </div>
            )}

            {/* Tab内容 */}
            <Tabs defaultActiveKey="1" type="card">
              {/* 参数Tab */}
              <TabPane 
                tab={
                  <span>
                    <SettingOutlined />
                    参数
                  </span>
                } 
                key="1"
              >
                {selectedTask.parameters ? (
                  <pre style={{ 
                    background: '#f5f5f5', 
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    padding: '16px',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    wordBreak: 'break-all',
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(selectedTask.parameters, null, 2)}
                  </pre>
                ) : (
                  <Empty description="无参数数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </TabPane>

              {/* 结果Tab */}
              <TabPane 
                tab={
                  <span>
                    <FileTextOutlined />
                    结果
                  </span>
                } 
                key="2"
              >
                <TaskResultRenderer task={selectedTask as any} showRaw={false} />
              </TabPane>

              {/* 原始数据Tab */}
              <TabPane 
                tab={
                  <span>
                    <DatabaseOutlined />
                    原始数据
                  </span>
                } 
                key="3"
              >
                <h4 style={{ marginBottom: '8px' }}>完整任务数据 (JSON)</h4>
                <pre style={{
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: '4px',
                  padding: '16px',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  maxHeight: '400px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(selectedTask, null, 2)}
                </pre>
              </TabPane>
            </Tabs>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProfilePage;